import { AlertCircle, RefreshCw } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { apiClient, ApiError } from '../services/api';
import { ChatMessage, ContextInfo, IssueAnalysisRequest, IssueAnalysisResponse } from '../types';
import { MessageList } from './MessageList';
import { QueryInput } from './QueryInput';
import { SearchContext } from './SearchContext';

const APP_CONFIG = {
    ENABLE_COPY_BY_DEFAULT: true,
    SHOW_COPY_TOGGLE: false,
};

export const ChatInterface: React.FC = () => {
    const [messages, setMessages] = useState<ChatMessage[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [collectedInfo, setCollectedInfo] = useState<{
        aircraft?: string;
        partName?: string;
        partIdentifier?: string;
        issueCategories?: string[];
        issueGroups?: string[];
        issueTopics?: string[];
        systems?: string[];
        sectionFilters?: string[];
        kpiFilters?: string[];
        maintenanceRunId?: string;
        logisticsRunId?: string;
        selectedPartIdentifier?: string;
        partKeywords?: string[];
    }>({});
    const [isCopyEnabled, setIsCopyEnabled] = useState(() => {
        const stored = localStorage.getItem('copy_enabled');
        if (stored === null) return APP_CONFIG.ENABLE_COPY_BY_DEFAULT;
        return stored === 'true';
    });
    const [activeSummaryLabel, setActiveSummaryLabel] = useState<string | null>(null);

    const prefillRef = useRef<(value: string) => void>();
    const pendingSummaryLabelRef = useRef<string | null>(null);
    const messagesEndRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    const initialized = useRef(false);

    useEffect(() => {
        if (initialized.current) return;
        initialized.current = true;

        const fetchWelcome = async () => {
            try {
                const request: IssueAnalysisRequest = { message: '' };
                const response = await apiClient.chat(request) as IssueAnalysisResponse;

                if (response) {
                    const { header, body } = splitFormattedIssues(response.formatted_issues);
                    const welcomeMsg: ChatMessage = {
                        id: generateId(),
                        content: response.answer || response.prompt || header || "Welcome to the Reliability Analysis Assistant.",
                        role: 'assistant',
                        timestamp: new Date(),
                        matchingAircraftCount: response.matching_aircraft_count,
                        matchingPartsCount: response.matching_parts_count,
                        matchingAircraftList: response.matching_aircraft_list,
                        matchingPartsList: response.matching_parts_list,
                        inQAMode: response.in_qa_mode,
                        formattedIssues: body
                    };
                    setMessages(prev => [welcomeMsg, ...prev]);


                }
            } catch (err) {
                console.error("Failed to fetch welcome message", err);
            }
        };
        fetchWelcome();
    }, []);


    const splitFormattedIssues = (formatted?: string) => {
        if (!formatted) return { header: '', body: '' };
        const lines = formatted.trim().split('\n');
        let header = '';
        let bodyStart = 0;

        for (let i = 0; i < lines.length; i++) {
            const line = lines[i].trim();
            if (line.startsWith('###')) {
                header = line;
                bodyStart = i + 1;
                break;
            }
        }

        if (!header) return { header: '', body: formatted };

        const body = lines.slice(bodyStart).join('\n').trim();
        return { header, body };
    };

    const generateId = () => {
        return Date.now().toString(36) + Math.random().toString(36).substr(2);
    };

    const fetchSummary = async (messageId: string, baseRequest: IssueAnalysisRequest, summaryLabel?: string | null) => {
        try {
            console.log('[FRONTEND] Fetching summary for message:', messageId);
            console.log('[FRONTEND] Base request:', baseRequest);
            const summaryRequest = { ...baseRequest, skip_summary: false };
            console.log('[FRONTEND] Summary request (skip_summary=false):', summaryRequest);
            const summaryResponse = await apiClient.chat(summaryRequest) as IssueAnalysisResponse;

            console.log('[FRONTEND] Summary response received:', {
                has_analysis_summary: !!summaryResponse.analysis_summary,
                analysis_summary_length: summaryResponse.analysis_summary?.length || 0,
                analysis_summary_preview: summaryResponse.analysis_summary?.substring(0, 100),
                full_response: summaryResponse
            });

            if (summaryResponse.analysis_summary) {
                console.log('[FRONTEND] Summary received, updating message');
                setMessages(prev => prev.map(msg =>
                    msg.id === messageId
                        ? {
                            ...msg,
                            analysisSummary: summaryResponse.analysis_summary,
                            recommendations: summaryResponse.recommendations,
                            summaryLoading: false
                        }
                        : msg
                ));
                if (summaryLabel) setActiveSummaryLabel(summaryLabel);
            } else if (summaryResponse.recommendations) {
                console.log('[FRONTEND] Recommendations received (no analysis), updating message');
                setMessages(prev => prev.map(msg =>
                    msg.id === messageId
                        ? { ...msg, recommendations: summaryResponse.recommendations, summaryLoading: false }
                        : msg
                ));
            } else {
                console.log('[FRONTEND] No summary in response, marking as complete');
                setMessages(prev => prev.map(msg =>
                    msg.id === messageId
                        ? { ...msg, summaryLoading: false }
                        : msg
                ));
            }
        } catch (err) {
            console.error('[FRONTEND] Error fetching summary:', err);
            setMessages(prev => prev.map(msg =>
                msg.id === messageId
                    ? { ...msg, summaryLoading: false }
                    : msg
            ));
        }
    };

    const handleSendMessage = async (content: string) => {
        if (!content.trim()) return;

        const summaryLabel = pendingSummaryLabelRef.current;
        pendingSummaryLabelRef.current = null;

        const mdsPattern = /\b([A-Z]\d{3}|[A-Z]-\d{2})\b/i;
        const mdsMatch = content.match(mdsPattern);
        const updatedCollectedInfo = { ...collectedInfo };
        if (mdsMatch && !updatedCollectedInfo.aircraft) {
            updatedCollectedInfo.aircraft = mdsMatch[1].toUpperCase();
            setCollectedInfo(updatedCollectedInfo);
        }

        const userMessage: ChatMessage = {
            id: generateId(),
            content,
            role: 'user',
            timestamp: new Date(),
        };

        setMessages(prev => [...prev, userMessage]);

        // Analysis commands: show interim message before sending request
        const trimmedLower = content.trim().toLowerCase();
        const isShowAnalysis = trimmedLower === 'show analysis';
        const isShowRecommendations = trimmedLower === 'show recommendations';
        if (isShowAnalysis || isShowRecommendations) {
            const partDesc = updatedCollectedInfo.partName || updatedCollectedInfo.selectedPartIdentifier || '';
            const interimMessage: ChatMessage = {
                id: generateId(),
                content: isShowAnalysis
                    ? `Generating analysis for **${partDesc}**...`
                    : `Generating recommendations for **${partDesc}**...`,
                role: 'assistant',
                timestamp: new Date(),
            };
            setMessages(prev => [...prev, interimMessage]);
        }

        setIsLoading(true);
        setError(null);

        try {
            const request: IssueAnalysisRequest = {
                aircraft: updatedCollectedInfo.aircraft,
                part_name: updatedCollectedInfo.partName,
                part_identifier: updatedCollectedInfo.partIdentifier,
                issue_categories: updatedCollectedInfo.issueCategories,
                issue_groups: updatedCollectedInfo.issueGroups,
                issue_topics: updatedCollectedInfo.issueTopics,
                systems: updatedCollectedInfo.systems,
                section_filters: updatedCollectedInfo.sectionFilters,
                kpi_filters: updatedCollectedInfo.kpiFilters,
                maintenance_run_id: updatedCollectedInfo.maintenanceRunId,
                logistics_run_id: updatedCollectedInfo.logisticsRunId,
                selected_part_identifier: updatedCollectedInfo.selectedPartIdentifier,
                part_keywords: updatedCollectedInfo.partKeywords,
                message: content,
                skip_summary: true,
            };

            const response = await apiClient.chat(request) as IssueAnalysisResponse;


            if (response.needs_input && response.selection_type) {
                if (response.context_info) {
                    const ctx: ContextInfo = response.context_info;
                    const newInfo = {
                        aircraft: ctx.aircraft !== undefined ? ctx.aircraft : collectedInfo.aircraft,
                        partName: ctx.part_name !== undefined ? ctx.part_name : collectedInfo.partName,
                        partIdentifier: ctx.part_identifier !== undefined ? ctx.part_identifier : collectedInfo.partIdentifier,
                        issueCategories: ctx.issue_categories !== undefined ? ctx.issue_categories : collectedInfo.issueCategories,
                        issueGroups: ctx.issue_groups !== undefined ? ctx.issue_groups : collectedInfo.issueGroups,
                        issueTopics: ctx.issue_topics !== undefined ? ctx.issue_topics : collectedInfo.issueTopics,
                        systems: ctx.systems !== undefined ? ctx.systems : collectedInfo.systems,
                        sectionFilters: ctx.section_filters !== undefined ? ctx.section_filters : collectedInfo.sectionFilters,
                        kpiFilters: ctx.kpi_filters !== undefined ? ctx.kpi_filters : collectedInfo.kpiFilters,
                        maintenanceRunId: ctx.maintenance_run_id !== undefined ? ctx.maintenance_run_id : collectedInfo.maintenanceRunId,
                        logisticsRunId: ctx.logistics_run_id !== undefined ? ctx.logistics_run_id : collectedInfo.logisticsRunId,
                        selectedPartIdentifier: ctx.selected_part_identifier !== undefined ? ctx.selected_part_identifier : collectedInfo.selectedPartIdentifier,
                        partKeywords: ctx.part_keywords !== undefined ? ctx.part_keywords : collectedInfo.partKeywords,
                    };
                    setCollectedInfo(newInfo);
                }
                console.log('[FRONTEND] Path 1: Building content from response');
                const { header, body } = splitFormattedIssues(response.formatted_issues);
                const content = response.prompt || response.answer || header || 'Identified Issues:';
                console.log('[FRONTEND] Path 1: Final content length:', content.length);

                const assistantMessage: ChatMessage = {
                    id: generateId(),
                    content,
                    role: 'assistant',
                    timestamp: new Date(),
                    selectionType: response.selection_type,
                    aircraftList: response.aircraft_list,
                    partsList: response.parts_list,
                    searchQuery: response.search_query,
                    needsRefinement: response.needs_refinement,
                    refinementPrompt: response.refinement_prompt,
                    matchingAircraftCount: response.matching_aircraft_count,
                    matchingPartsCount: response.matching_parts_count,
                    matchingAircraftList: response.matching_aircraft_list,
                    matchingPartsList: response.matching_parts_list,
                    inQAMode: response.in_qa_mode,
                    refinementData: response.refinement_data,
                    summaryLoading: false,
                    formattedIssues: body
                };
                setMessages(prev => [...prev, assistantMessage]);
            } else if (response.needs_input) {
                if (response.context_info) {
                    const ctx: ContextInfo = response.context_info;
                    const newInfo = {
                        aircraft: ctx.aircraft !== undefined ? ctx.aircraft : collectedInfo.aircraft,
                        partName: ctx.part_name !== undefined ? ctx.part_name : collectedInfo.partName,
                        partIdentifier: ctx.part_identifier !== undefined ? ctx.part_identifier : collectedInfo.partIdentifier,
                        issueCategories: ctx.issue_categories !== undefined ? ctx.issue_categories : collectedInfo.issueCategories,
                        issueGroups: ctx.issue_groups !== undefined ? ctx.issue_groups : collectedInfo.issueGroups,
                        issueTopics: ctx.issue_topics !== undefined ? ctx.issue_topics : collectedInfo.issueTopics,
                        systems: ctx.systems !== undefined ? ctx.systems : collectedInfo.systems,
                        sectionFilters: ctx.section_filters !== undefined ? ctx.section_filters : collectedInfo.sectionFilters,
                        kpiFilters: ctx.kpi_filters !== undefined ? ctx.kpi_filters : collectedInfo.kpiFilters,
                        maintenanceRunId: ctx.maintenance_run_id !== undefined ? ctx.maintenance_run_id : collectedInfo.maintenanceRunId,
                        logisticsRunId: ctx.logistics_run_id !== undefined ? ctx.logistics_run_id : collectedInfo.logisticsRunId,
                        selectedPartIdentifier: ctx.selected_part_identifier !== undefined ? ctx.selected_part_identifier : collectedInfo.selectedPartIdentifier,
                        partKeywords: ctx.part_keywords !== undefined ? ctx.part_keywords : collectedInfo.partKeywords,
                    };
                    setCollectedInfo(newInfo);
                }
                const assistantMessage: ChatMessage = {
                    id: generateId(),
                    content: response.prompt || response.answer || '',
                    role: 'assistant',
                    timestamp: new Date(),
                    partsList: response.parts_list,
                };
                setMessages(prev => [...prev, assistantMessage]);
            } else {
                const hasQuadrantcoming = response.show_quadrant && (
                    (response.issues && response.issues.length > 0) ||
                    response.context_info?.maintenance_run_id ||
                    response.context_info?.logistics_run_id
                );

                if (response.part_confirmation && !hasQuadrantcoming) {
                    const confirmMsg: ChatMessage = {
                        id: generateId(),
                        content: response.part_confirmation,
                        role: 'assistant',
                        timestamp: new Date(),
                        partsList: response.parts_list,
                        summaryLoading: false,
                        matchingPartsCount: response.matching_parts_count,
                        matchingPartsList: response.matching_parts_list,
                        isConfirmation: true,
                    };
                    setMessages(prev => [...prev, confirmMsg]);
                    response.parts_list = undefined;
                }

                if (response.context_cleared) {
                    setCollectedInfo({});
                    const assistantMessage: ChatMessage = {
                        id: generateId(),
                        content: response.answer || response.formatted_issues || response.prompt || 'Context cleared.',
                        role: 'assistant',
                        timestamp: new Date(),
                        issues: response.issues,
                        showQuadrant: response.show_quadrant,
                        matchingAircraftCount: response.matching_aircraft_count,
                        matchingPartsCount: response.matching_parts_count,
                        matchingAircraftList: response.matching_aircraft_list,
                        matchingPartsList: response.matching_parts_list,
                        inQAMode: response.in_qa_mode,
                        partConfirmation: hasQuadrantcoming ? response.part_confirmation : undefined,
                    };
                    setMessages(prev => [...prev, assistantMessage]);
                } else if (response.context_info) {
                    const ctx: ContextInfo = response.context_info;
                    const newInfo = {
                        aircraft: ctx.aircraft !== undefined ? ctx.aircraft : collectedInfo.aircraft,
                        partName: ctx.part_name !== undefined ? ctx.part_name : collectedInfo.partName,
                        partIdentifier: ctx.part_identifier !== undefined ? ctx.part_identifier : collectedInfo.partIdentifier,
                        issueCategories: ctx.issue_categories !== undefined ? ctx.issue_categories : collectedInfo.issueCategories,
                        issueGroups: ctx.issue_groups !== undefined ? ctx.issue_groups : collectedInfo.issueGroups,
                        issueTopics: ctx.issue_topics !== undefined ? ctx.issue_topics : collectedInfo.issueTopics,
                        systems: ctx.systems !== undefined ? ctx.systems : collectedInfo.systems,
                        sectionFilters: ctx.section_filters !== undefined ? ctx.section_filters : collectedInfo.sectionFilters,
                        kpiFilters: ctx.kpi_filters !== undefined ? ctx.kpi_filters : collectedInfo.kpiFilters,
                        maintenanceRunId: ctx.maintenance_run_id !== undefined ? ctx.maintenance_run_id : collectedInfo.maintenanceRunId,
                        logisticsRunId: ctx.logistics_run_id !== undefined ? ctx.logistics_run_id : collectedInfo.logisticsRunId,
                        selectedPartIdentifier: ctx.selected_part_identifier !== undefined ? ctx.selected_part_identifier : collectedInfo.selectedPartIdentifier,
                        partKeywords: ctx.part_keywords !== undefined ? ctx.part_keywords : collectedInfo.partKeywords,
                    };
                    setCollectedInfo(newInfo);
                    console.log('[FRONTEND] Path 0 (context): Building content from response');
                    const { header, body } = splitFormattedIssues(response.formatted_issues);
                    const contextContent = (response.analysis_summary || response.recommendations || response.show_quadrant)
                        ? ''
                        : (response.answer || response.prompt || header || 'Current context retrieved.');
                    const messageId = generateId();
                    const assistantMessage: ChatMessage = {
                        id: messageId,
                        content: contextContent,
                        role: 'assistant',
                        timestamp: new Date(),
                        issues: response.issues,
                        showQuadrant: response.show_quadrant,
                        formattedIssues: body,
                        formattedIssuesHeader: header || undefined,
                        matchingAircraftCount: response.matching_aircraft_count,
                        matchingPartsCount: response.matching_parts_count,
                        matchingAircraftList: response.matching_aircraft_list,
                        matchingPartsList: response.matching_parts_list,
                        inQAMode: response.in_qa_mode,
                        analysisSummary: response.analysis_summary || response.results?.[0]?.analysis_summary || response.results?.[0]?.metadata?.analysis_summary,
                        recommendations: response.recommendations,
                        partName: response.context_info?.part_name || updatedCollectedInfo.partName || response.context_info?.selected_part_identifier || updatedCollectedInfo.partIdentifier,
                        summaryLoading: response.summary_pending || false,
                        searchQuery: response.search_query,
                        maintenanceRunId: newInfo.maintenanceRunId,
                        logisticsRunId: newInfo.logisticsRunId,
                        partConfirmation: hasQuadrantcoming ? response.part_confirmation : undefined,
                    };
                    setMessages(prev => [...prev, assistantMessage]);

                    console.log('[FRONTEND] Checking summary_pending (Path 0):', response.summary_pending);
                    if (response.summary_pending) {
                        console.log('[FRONTEND] Summary pending, calling fetchSummary for message:', messageId);
                        fetchSummary(messageId, request, summaryLabel);
                    } else {
                        if (summaryLabel) setActiveSummaryLabel(summaryLabel);
                        console.log('[FRONTEND] No summary_pending flag in Path 0, skipping fetchSummary');
                    }
                } else {
                    console.log('[FRONTEND] Path 2: Building content from response');
                    const { header, body } = splitFormattedIssues(response.formatted_issues);
                    let content = response.answer || response.prompt || header;
                    if (!content && response.issues && response.issues.length > 0) {
                        content = `Found ${response.issues.length} flagged issue(s) in Executive reports.`;
                    }
                    if (!content) content = 'No issues found.';

                    console.log('Response from backend:', {
                        formatted_issues: response.formatted_issues?.substring(0, 200),
                        issues_count: response.issues?.length || 0,
                        issues: response.issues,
                        analysis_summary: response.analysis_summary,
                    });

                    const messageId = generateId();
                    const assistantMessage: ChatMessage = {
                        id: messageId,
                        content,
                        role: 'assistant',
                        timestamp: new Date(),
                        issues: response.issues,
                        showQuadrant: response.show_quadrant,
                        formattedIssues: body,
                        formattedIssuesHeader: header || undefined,
                        analysisSummary: response.analysis_summary || response.results?.[0]?.analysis_summary || response.results?.[0]?.metadata?.analysis_summary,
                        matchingAircraftCount: response.matching_aircraft_count,
                        matchingPartsCount: response.matching_parts_count,
                        matchingAircraftList: response.matching_aircraft_list,
                        matchingPartsList: response.matching_parts_list,
                        inQAMode: response.in_qa_mode,
                        recommendations: response.recommendations,
                        partName: updatedCollectedInfo.partName || updatedCollectedInfo.partIdentifier,
                        summaryLoading: response.summary_pending || false,
                        searchQuery: response.search_query,
                        maintenanceRunId: updatedCollectedInfo.maintenanceRunId,
                        logisticsRunId: updatedCollectedInfo.logisticsRunId,
                        partConfirmation: hasQuadrantcoming ? response.part_confirmation : undefined,
                    };
                    console.log('Assistant message created:', {
                        has_issues: !!assistantMessage.issues,
                        issues_count: assistantMessage.issues?.length || 0,
                        first_issue: assistantMessage.issues?.[0]
                    });
                    setMessages(prev => [...prev, assistantMessage]);

                    console.log('[FRONTEND] Checking summary_pending:', response.summary_pending);
                    if (response.summary_pending) {
                        console.log('[FRONTEND] Summary pending, calling fetchSummary for message:', messageId);
                        fetchSummary(messageId, request, summaryLabel);
                    } else {
                        if (summaryLabel) setActiveSummaryLabel(summaryLabel);
                        console.log('[FRONTEND] No summary_pending flag, skipping fetchSummary');
                    }

                    if (response.context_info) {
                        const ctx: ContextInfo = response.context_info;
                        const newInfo = {
                            aircraft: ctx.aircraft !== undefined ? ctx.aircraft : collectedInfo.aircraft,
                            partName: ctx.part_name !== undefined ? ctx.part_name : collectedInfo.partName,
                            partIdentifier: ctx.part_identifier !== undefined ? ctx.part_identifier : collectedInfo.partIdentifier,
                            issueCategories: ctx.issue_categories !== undefined ? ctx.issue_categories : collectedInfo.issueCategories,
                            issueGroups: ctx.issue_groups !== undefined ? ctx.issue_groups : collectedInfo.issueGroups,
                            issueTopics: ctx.issue_topics !== undefined ? ctx.issue_topics : collectedInfo.issueTopics,
                            systems: ctx.systems !== undefined ? ctx.systems : collectedInfo.systems,
                            sectionFilters: ctx.section_filters !== undefined ? ctx.section_filters : collectedInfo.sectionFilters,
                            kpiFilters: ctx.kpi_filters !== undefined ? ctx.kpi_filters : collectedInfo.kpiFilters,
                            maintenanceRunId: ctx.maintenance_run_id !== undefined ? ctx.maintenance_run_id : collectedInfo.maintenanceRunId,
                            logisticsRunId: ctx.logistics_run_id !== undefined ? ctx.logistics_run_id : collectedInfo.logisticsRunId,
                            selectedPartIdentifier: ctx.selected_part_identifier !== undefined ? ctx.selected_part_identifier : collectedInfo.selectedPartIdentifier,
                            partKeywords: ctx.part_keywords !== undefined ? ctx.part_keywords : collectedInfo.partKeywords,
                        };
                        setCollectedInfo(newInfo);
                    }
                }
            }

            if (response.error && !response.error.includes("No Executive reports found")) {
                setError(response.error);
            }
        } catch (err) {
            console.error('Error sending message:', err);

            let errorMessage = 'Failed to send message. Please try again.';
            if (err instanceof ApiError) {
                if (err.status === 400) {
                    errorMessage = 'Invalid request. Please check your input.';
                } else if (err.status === 500) {
                    errorMessage = 'Server error. Please try again later.';
                } else {
                    errorMessage = err.message;
                }
            }

            setError(errorMessage);
        } finally {
            setIsLoading(false);
        }
    };

    const handleRetry = () => {
        setError(null);
        const lastUserMessage = [...messages].reverse().find(m => m.role === 'user');
        if (lastUserMessage) {
            handleSendMessage(lastUserMessage.content);
        }
    };





    const handleClearSummary = () => {
        setActiveSummaryLabel(null);
        let quadrantMsg: ChatMessage | undefined;
        for (let i = messages.length - 1; i >= 0; i--) {
            if (messages[i].showQuadrant) { quadrantMsg = messages[i]; break; }
        }
        if (quadrantMsg) {
            const newMsg: ChatMessage = {
                id: generateId(),
                content: '',
                role: 'assistant',
                timestamp: new Date(),
                issues: quadrantMsg.issues,
                showQuadrant: true,
                maintenanceRunId: quadrantMsg.maintenanceRunId,
                logisticsRunId: quadrantMsg.logisticsRunId,
            };
            setMessages(prev => [...prev, newMsg]);
        }
    };

    const handleClearContext = async () => {
        const clearMessage = "clear";

        const userMessage: ChatMessage = {
            id: generateId(),
            content: clearMessage,
            role: 'user',
            timestamp: new Date(),
        };

        setMessages(prev => [...prev, userMessage]);
        setIsLoading(true);
        setError(null);

        try {
            const request: IssueAnalysisRequest = {
                message: clearMessage,
                aircraft: collectedInfo.aircraft,
                issue_categories: collectedInfo.issueCategories,
            };

            await apiClient.chat(request);
            setCollectedInfo({});
            setActiveSummaryLabel(null);


            const assistantMessage: ChatMessage = {
                id: generateId(),
                content: 'Context cleared. You can now start a new query.',
                role: 'assistant',
                timestamp: new Date(),
            };

            setMessages(prev => [...prev, assistantMessage]);
        } catch (err) {
            const apiError = err as ApiError;
            setError(apiError.message || 'Failed to clear context');
        } finally {
            setIsLoading(false);
        }
    };

    const toggleCopy = () => {
        const newValue = !isCopyEnabled;
        setIsCopyEnabled(newValue);
        localStorage.setItem('copy_enabled', String(newValue));
    };

    return (
        <div className={`chat-interface ${isCopyEnabled ? 'copy-enabled' : ''}`}>
            {APP_CONFIG.SHOW_COPY_TOGGLE && (
                <div className="chat-controls">
                    <button
                        onClick={toggleCopy}
                        className={`copy-toggle-btn ${isCopyEnabled ? 'active' : ''}`}
                        title={isCopyEnabled ? "Disable Click-to-Copy" : "Enable Click-to-Copy"}
                    >
                        {isCopyEnabled ? "Disable Copy" : "Enable Copy"}
                    </button>
                </div>
            )}
            <SearchContext
                context={{
                    aircraft: collectedInfo.aircraft,
                    part_name: collectedInfo.partName,
                    part_identifier: collectedInfo.partIdentifier,
                    issue_categories: collectedInfo.issueCategories,
                    issue_groups: collectedInfo.issueGroups,
                    issue_topics: collectedInfo.issueTopics,
                    systems: collectedInfo.systems,
                    maintenance_run_id: collectedInfo.maintenanceRunId,
                    logistics_run_id: collectedInfo.logisticsRunId,
                    selected_part_identifier: collectedInfo.selectedPartIdentifier,
                    part_keywords: collectedInfo.partKeywords,
                }}
                onClear={handleClearContext}
                activeSummaryLabel={activeSummaryLabel}
                onClearSummary={handleClearSummary}
            />

            {/* ContextCountsHeader hidden - can be re-enabled later
            <ContextCountsHeader
                partsCount={lastCounts?.parts || 0}
                aircraftCount={lastCounts?.aircraft || 0}
                isVisible={!!lastCounts}
            />
            */}

            <div className="chat-messages">
                <MessageList
                    messages={messages}
                    onPrefillInput={(v) => {
                        prefillRef.current?.(v);
                        pendingSummaryLabelRef.current = v;
                    }}
                />
                {isLoading && (
                    <div className="loading-message">
                        <div className="loading-avatar">
                            <div className="typing-indicator">
                                <div className="typing-dot"></div>
                                <div className="typing-dot"></div>
                                <div className="typing-dot"></div>
                            </div>
                        </div>
                        <div className="loading-text">Thinking...</div>
                    </div>
                )}
                <div ref={messagesEndRef} />
            </div>

            {error && (
                <div className="error-banner">
                    <AlertCircle size={20} />
                    <span>{error}</span>
                    <button onClick={handleRetry} className="retry-button">
                        <RefreshCw size={16} />
                        Retry
                    </button>
                </div>
            )}

            <div className="chat-input">
                <QueryInput
                    onSendMessage={handleSendMessage}
                    isLoading={isLoading}
                    disabled={!!error}
                    registerPrefill={(fn) => { prefillRef.current = fn; }}
                />
            </div>
        </div>
    );
};
